import "@google/model-viewer";
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { fetchModelByUrl } from "../api/modelApi";
import { fetchItemByModelName } from "../api/itemApi";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { standardSizes } from "../constants/itemOptions";
import "../css/ARViewerV2.css";

const ARViewerV2 = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const modelName = queryParams.get("model");

  const [modelBlob, setModelBlob] = useState(null);
  const [usdzUrl, setUsdzUrl] = useState(null);
  const [scaleFactor, setScaleFactor] = useState(1);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [scaleZ, setScaleZ] = useState(1);
  const [itemData, setItemData] = useState(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [showDimensions, setShowDimensions] = useState(false);

  const initialized = useRef(false);
  const modelViewerRef = useRef(null);
  const scalingApplied = useRef(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const initialize = async () => {
    if (!modelName) return;

    try {
      console.log(`Fetching item details for model: ${modelName}`);
      const item = await fetchItemByModelName(modelName);
      if (!item) {
        console.error("Item details not found.");
        return;
      }
      setItemData(item);
      console.log("Fetched item data:", item);

      console.log("Fetching model from backend:", modelName);
      const glbBlob = await fetchModelByUrl(modelName);
      const glbURL = URL.createObjectURL(glbBlob);
      setModelBlob(glbURL);

      // Wait for model to load in <model-viewer>
      await waitForModelLoad();

      // Apply scaling if necessary
      console.log("Applying scaling:", item.autoScale);
      if (item.autoScale) {
        applyScaling(item);
      }

      // Convert to USDZ if running on iOS
      if (isIOS) {
        convertGLBtoUSDZ(glbURL, item.autoScale);
      }
    } catch (error) {
      console.error("Error initializing AR Viewer:", error);
    }
  };

  const waitForModelLoad = () =>
    new Promise((resolve) => {
      if (!modelViewerRef.current) return resolve();

      const modelViewer = modelViewerRef.current;
      modelViewer.addEventListener(
        "load",
        () => {
          console.log("Model loaded successfully.");
          setModelLoaded(true);
          resolve();
        },
        { once: true }
      );
    });

  const applyScaling = (item) => {
    if (!modelViewerRef.current || !item || scalingApplied.current) return;

    const modelViewer = modelViewerRef.current;

    // 1. Get model dimensions (bounding box in meters)
    const {
      x: modelWidth,
      y: modelHeight,
      z: modelDepth,
    } = modelViewer.getDimensions();

    // 2. Get real-world target dimensions from the item (in cm → convert to meters)
    const { width, height, depth } = item;

    if (!width || !height || !depth) {
      console.warn("Missing dimension(s) on item — skipping scaling.");
      return;
    }

    const targetWidth = width / 100;
    const targetHeight = height / 100;
    const targetDepth = depth / 100;

    // 3. Compute scale factors per axis
    const scaleX = targetWidth / modelWidth;
    const scaleY = targetHeight / modelHeight;
    const scaleZ = targetDepth / modelDepth;

    // 4. Apply scaling to the model viewer
    //modelViewer.setAttribute("scale", `${scaleX} ${scaleY} ${scaleZ}`);
    setScaleX(scaleX);
    setScaleY(scaleY);
    setScaleZ(scaleZ);

    const minScale = Math.min(scaleX, scaleY, scaleZ);
    const cameraDistance = Math.max(0.1, 2 * minScale);
    modelViewer.setAttribute("camera-orbit", `0deg 75deg ${cameraDistance}m`);

    const hotspot = modelViewer.querySelector('button[slot="hotspot-1"]');
    const boundingBox = modelViewer.getBoundingBox();
    if (hotspot) {
      const centerY =
        boundingBox.min.y + (boundingBox.max.y - boundingBox.min.y) / 2;
      const hotspotY = centerY + targetHeight / 200; // tweak this factor if needed
      hotspot.setAttribute("data-position", `0 ${hotspotY} 0`);
    }

    scalingApplied.current = true;
  };

  const convertGLBtoUSDZ = async (glbUrl, autoScale) => {
    try {
      const loader = new GLTFLoader();
      const glbBlobResponse = await fetch(glbUrl);
      const glbArrayBuffer = await glbBlobResponse.arrayBuffer();

      loader.parse(
        glbArrayBuffer,
        "",
        async (gltf) => {
          const { scene } = gltf;
          const newScale = autoScale ? 1 : scaleFactor;
          scene.scale.set(newScale, newScale, newScale);

          const exporter = new USDZExporter();
          const arraybuffer = await exporter.parseAsync(scene, {
            maxTextureSize: 2048,
          });

          const usdzBlob = new Blob([arraybuffer], {
            type: "model/vnd.usdz+zip",
          });
          const usdzURL = URL.createObjectURL(usdzBlob);
          console.log("USDZ successfully generated:", usdzURL);
          setUsdzUrl(usdzURL);
        },
        (error) => {
          console.error("Error parsing GLB model:", error);
        }
      );
    } catch (error) {
      console.error("USDZ conversion failed:", error);
    }
  };

  useEffect(() => {
    if (!modelViewerRef.current || !modelBlob) return;

    const modelViewer = modelViewerRef.current;

    function drawLine(svgLine, dotHotspot1, dotHotspot2) {
      if (dotHotspot1 && dotHotspot2) {
        svgLine.setAttribute("x1", dotHotspot1.canvasPosition.x);
        svgLine.setAttribute("y1", dotHotspot1.canvasPosition.y);
        svgLine.setAttribute("x2", dotHotspot2.canvasPosition.x);
        svgLine.setAttribute("y2", dotHotspot2.canvasPosition.y);
      }
    }

    function updateDimensions() {
      if (!modelViewer) return;

      const center = modelViewer.getBoundingBoxCenter();
      const size = modelViewer.getDimensions();

      if (!size || !size.x || !size.y || !size.z) {
        console.error("Model dimensions not available yet.");
        return;
      }

      const x2 = size.x / 2;
      const y2 = size.y / 2;
      const z2 = size.z / 2;

      // Set dimension labels slightly outside the model
      modelViewer.updateHotspot({
        name: "hotspot-dim-Length",
        position: `${center.x + x2 * 1.2} ${center.y} ${center.z}`,
      });
      modelViewer.updateHotspot({
        name: "hotspot-dim-Width",
        position: `${center.x} ${center.y} ${center.z - z2 * 1.2}`,
      });
      modelViewer.updateHotspot({
        name: "hotspot-dim-Height",
        position: `${center.x} ${center.y + y2 * 1.2} ${center.z}`,
      });

      // Set text for dimensions
      modelViewer.querySelector(
        'button[slot="hotspot-dim-Length"]'
      ).textContent = `length: ${(size.x * 100).toFixed(0)} cm`;
      modelViewer.querySelector(
        'button[slot="hotspot-dim-Width"]'
      ).textContent = `width: ${(size.z * 100).toFixed(0)} cm`;
      modelViewer.querySelector(
        'button[slot="hotspot-dim-Height"]'
      ).textContent = `height: ${(size.y * 100).toFixed(0)} cm`;

      // Update SVG lines dynamically
      const dimLines = modelViewer.querySelectorAll("line");
      if (dimLines.length >= 3) {
        drawLine(
          dimLines[0],
          modelViewer.queryHotspot("hotspot-dim-Length"),
          modelViewer.queryHotspot("hotspot-dim-Width")
        );
        drawLine(
          dimLines[1],
          modelViewer.queryHotspot("hotspot-dim-Width"),
          modelViewer.queryHotspot("hotspot-dim-Height")
        );
        drawLine(
          dimLines[2],
          modelViewer.queryHotspot("hotspot-dim-Height"),
          modelViewer.queryHotspot("hotspot-dim-Length")
        );
      }
    }

    function updateDimensionsSafely() {
      setTimeout(() => {
        if (!modelViewer || !modelViewer.queryHotspot) return;
        updateDimensions();
      }, 500); // Small delay ensures elements exist
    }

    modelViewer.addEventListener("load", updateDimensionsSafely);
    modelViewer.addEventListener("camera-change", updateDimensionsSafely);

    return () => {
      modelViewer.removeEventListener("load", updateDimensionsSafely);
      modelViewer.removeEventListener("camera-change", updateDimensionsSafely);
    };
  }, [modelBlob]);

  useEffect(() => {
    const dimElements = document.querySelectorAll(
      ".dim, .dimensionLineContainer"
    );
    dimElements.forEach((el) => {
      if (showDimensions) {
        el.classList.remove("hide");
      } else {
        el.classList.add("hide");
      }
    });
  }, [showDimensions]);

  useEffect(() => {
    if (!initialized.current) {
      initialize();
      initialized.current = true;
    }
  }, []);

  useEffect(() => {
    console.log(
      "model-viewer AR Support:",
      modelViewerRef.current?.canActivateAR
    );
  }, [modelViewerRef]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100vh" }}>
      <model-viewer
        ref={modelViewerRef}
        src={modelBlob}
        alt="3D Model"
        auto-rotate
        camera-controls
        ar
        ar-scale="fixed"
        ar-modes="quick-look scene-viewer web-xr"
        environment-image="/table2.hdr"
        shadow-intensity="1"
        exposure="1.5"
        tone-mapping="aces"
        min-camera-orbit="-180deg 60deg auto"
        max-camera-orbit="-60deg 90deg auto"
        scale={
          itemData && !itemData.autoScale
            ? "1 1 1"
            : `${scaleX} ${scaleY} ${scaleZ}`
        }
        style={{ width: "100%", height: "100vh" }}
      >
        <button className="ARViewerV2-activate-ar-btn" slot="ar-button">
          activate AR
        </button>
        {itemData && (
          <button
            slot="hotspot-1"
            data-position="0 0.5 0"
            data-normal="0 1 0"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              border: "none",
              padding: "10px",
              borderRadius: "10px",
              cursor: "pointer",
              maxWidth: "200px",
              textAlign: "left",
            }}
          >
            <div className="ARViewerV2-item-description-container">
              <div className="ARViewerV2-title-price-container">
                <p className="ARViewerV2-title">{itemData.name}</p>
                <p className="ARViewerV2-price">${itemData.price.toFixed(2)}</p>
              </div>
              <p className="ARViewerV2-description">{itemData.description}</p>
              {itemData.dietaryLabels?.length > 0 && (
                <p className="ARViewerV2-dietary-labels">
                  <span style={{ fontFamily: "Hiragino Kaku Gothic StdN" }}>
                    dietary labels:
                  </span>{" "}
                  {itemData.dietaryLabels.join(", ")}
                </p>
              )}
            </div>
          </button>
        )}
        <button
          className="ARViewerV2-dimension-btn"
          slot="hotspot-dim-Length"
          class="dim"
          data-position="0 0 0"
          data-normal="1 0 0"
        >
          loading...
        </button>
        <button
          className="ARViewerV2-dimension-btn"
          slot="hotspot-dim-Width"
          class="dim"
          data-position="0 0 0"
          data-normal="0 0 1"
        >
          loading...
        </button>
        <button
          className="ARViewerV2-dimension-btn"
          slot="hotspot-dim-Height"
          class="dim"
          data-position="0 0 0"
          data-normal="0 1 0"
        >
          loading...
        </button>

        <svg
          id="dimLines"
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
          class="dimensionLineContainer hide"
        >
          <line class="dimensionLine"></line>
          <line class="dimensionLine"></line>
          <line class="dimensionLine"></line>
        </svg>

        <button
          slot="hotspot-show-dimensions"
          className="dim-toggle-button"
          data-position="0 -0.6 0"
          data-normal="0 1 0"
          onClick={() => setShowDimensions(!showDimensions)}
        >
          {showDimensions ? "hide dimensions" : "show dimensions"}
        </button>
      </model-viewer>
    </div>
  );
};

export default ARViewerV2;
